import React, { useContext } from 'react'

import FormContainer from 'components/FormContainer'
import { TestIDs } from 'test/utils/testIds'
import { FormikValues, FormikProps } from 'formik'

import {
  FormItem,
  FormRow,
  HeroHeader,
  LoginText,
  StyledText,
  StyledTextInput,
  GetStartedButtonContainer,
} from './styled-components'
import { AppliedCodeCard } from '../../components/PromoCodes/AppliedCodeCard'
import { PromoCodesDrawer } from '../../components/PromoCodes/PromoCodesDrawer'
import { PromoCodesProps } from '../../components/PromoCodes/usePromoCodes'
import { getWindow, useAnalyticsClient, useRemoteStrings } from '@acorns/web-utils'
import { GetStartedButton, ButtonHandle } from 'pages/Signup/components/GetStartedButton/index'
import { getSourceAttributes } from 'utils/sourceAttributes'
import Environment from 'utils/environment'
import { trackRegistrationCreateAccountExistingAccountLoginLinkTappedPayload } from 'generated/trackingEvents'
import { translateEventProps } from 'utils/analytics/eventSpec'
import { AcornsEarlyContext } from '../../../../providers/AcornsEarlyProvider/index'
import { AcornsEarlySupplement } from '../../components/AcornsEarlySupplement/index'
import { theme } from '@acorns/grove'

interface EmailViewProps {
  onEmailChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  onFocus: () => void
  onBlur: () => void
  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void
  promoCodeProps: PromoCodesProps & {
    isPromoCodeShowing: boolean
  }
  formik: FormikProps<FormikValues>
  isEmailFieldFocused: boolean
  getStartedButtonRef: React.RefObject<ButtonHandle>
  env: Environment
  isEmailValid: boolean
  shouldGrayOutEmailCTA: boolean
  setIsEmailRowDisplayed: (isEmailRowDisplayed: boolean) => void
  setIsEmailValid: (isEmailValid: boolean) => void
  setSubmitEmailCount: (submitEmailCount: number) => void
  submitEmailCount: number
  validPromoCode: string
  earlyCustomerEmail?: string
}

export const EmailView = ({
  onEmailChange,
  onFocus,
  onBlur,
  onKeyDown,
  promoCodeProps,
  formik,
  isEmailFieldFocused,
  getStartedButtonRef,
  isEmailValid,
  shouldGrayOutEmailCTA,
  setIsEmailRowDisplayed,
  setIsEmailValid,
  setSubmitEmailCount,
  submitEmailCount,
  validPromoCode,
  env,
  earlyCustomerEmail,
}: EmailViewProps) => {
  const analytics = useAnalyticsClient()
  const { isAcornsEarlyGoldCustomerFlow } = useContext(AcornsEarlyContext)
  const { getRemoteString, StyledRemoteString } = useRemoteStrings()

  const handleLoginClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    getWindow().location.assign(env.get('loginUrl'))
    const [eventName, eventProperties] =
      trackRegistrationCreateAccountExistingAccountLoginLinkTappedPayload()

    analytics.track(eventName, translateEventProps(eventProperties))
  }
  return (
    <>
      <HeroHeader>
        <StyledText>
          {getRemoteString({ key: 'registration.landing_page.signup.title' })}
        </StyledText>
      </HeroHeader>
      <FormContainer>
        <FormRow>
          <FormItem>
            <StyledTextInput
              label={{
                value: 'Email',
              }}
              className="text-input"
              inputProps={{
                name: 'email',
                value: earlyCustomerEmail || formik.values.email,
                onChange: onEmailChange,
                id: 'email',
                type: 'email',
                placeholder: 'Email address',
                'data-testid': TestIDs.SIGNUP.INPUT_EMAIL,
                onFocus,
                onBlur,
                onKeyDown,
                disabled: !!earlyCustomerEmail,
              }}
              error={!!formik.errors.email}
              errorText={formik.errors.email as string}
            />
            <GetStartedButtonContainer>
              <GetStartedButton
                formValues={formik.values}
                isEmailFieldFocused={isEmailFieldFocused}
                getSourceAttributes={getSourceAttributes}
                inputRef={getStartedButtonRef}
                isEmailValid={isEmailValid && !shouldGrayOutEmailCTA}
                isSubmitting={formik.isSubmitting}
                setErrors={formik.setErrors}
                setIsEmailRowDisplayed={setIsEmailRowDisplayed}
                setIsEmailValid={setIsEmailValid}
                setSubmitEmailCount={setSubmitEmailCount}
                submitEmailCount={submitEmailCount}
                validateForm={formik.validateForm}
                validPromoCode={validPromoCode}
              />
            </GetStartedButtonContainer>
            <LoginText>
              <StyledRemoteString
                stringKey={'registration.landing_page.signup.existing_account.button.markdown'}
                modifiers={[
                  {
                    color: theme.colors.primaryGreen,
                    textDecoration: 'none',
                    onClick: handleLoginClick,
                  },
                ]}
              />
            </LoginText>
          </FormItem>
        </FormRow>
      </FormContainer>
      {isAcornsEarlyGoldCustomerFlow ? (
        <AcornsEarlySupplement />
      ) : (
        <>
          <AppliedCodeCard
            appliedPromoCode={promoCodeProps.appliedPromoCode}
            condensedTitle={promoCodeProps.condensedTitle}
            hasPromoCodeQueryParam={promoCodeProps.hasPromoCodeQueryParam}
            isPromoCodeVerificationLoading={promoCodeProps.isPromoCodeVerificationLoading}
            promoCodeChannel={promoCodeProps.promoCodeChannel}
            setIsDrawerOpen={promoCodeProps.setIsDrawerOpen}
          />
          <PromoCodesDrawer
            appliedPromoCode={promoCodeProps.appliedPromoCode}
            description={promoCodeProps.description}
            isDrawerOpen={promoCodeProps.isDrawerOpen}
            isErrorSubTextShown={promoCodeProps.isErrorSubTextShown}
            isPromoCodeVerificationLoading={promoCodeProps.isPromoCodeVerificationLoading}
            lastValidPromoCode={promoCodeProps.lastValidPromoCode}
            onCloseDrawer={promoCodeProps.onCloseDrawer}
            termsUrl={promoCodeProps.termsUrl}
            validatePromoCode={promoCodeProps.validatePromoCode}
            validationState={promoCodeProps.validationState}
          />
        </>
      )}
    </>
  )
}
